/**
 * Строка референса в «Сценах» на `/generate`: картинка, библиотека и категория,
 * размер (мелкая картинка — подсказка, запуск не блокирует) и поле «что учесть».
 *
 * Поле — текст ЭТОГО запуска, а не заметка референса: открывается пустым (в заметке бывают
 * данные каталога — модель, ссылка — и оценки вроде «ракурс проваливается»), в референс не
 * пишется, уходит в тело запуска и в снимок. Заметка — строкой под полем, «↺ из заметки» подставляет её.
 *
 * Роль «сцена | внешность модели» (задача Оли №318: «пусть будет библиотека лиц»): референс-лицо
 * перестаёт быть сценой и уходит входом портрета в каждую пару, поэтому у него нет ни «что учесть»
 * (подсказку портрета пишет сервис), ни предупреждения о мелкой картинке, ни счётчика генераций
 * сцены — всё это про сцену.
 */
import { X } from "lucide-react";
import { Link } from "react-router";

import { SceneCounter } from "@/components/fitting/SceneResultsSheet";
import { btnGhost, input, label } from "@/components/trends/ui";
import { thumbUrl } from "@/lib/media";
import type { ReferenceLite } from "@/lib/references";

/** `REFERENCE_NOTE_MAX` сервиса. */
const NOTE_MAX = 1000;
/** Длинная сторона, ниже которой картинка мелковата для сцены. */
const SMALL_SIDE = 1000;

/** Роль референса на странице: сцена запуска или внешность модели (№318). */
export type RefRole = "scene" | "face";

const ROLE_OPTIONS: { value: RefRole; label: string }[] = [
  { value: "scene", label: "сцена" },
  { value: "face", label: "внешность модели" },
];
const ROLE_HINT =
  "сцена: по ней строится кадр · внешность модели: с картинки берутся лицо, причёска и типаж, сценой она не будет";
const roleSelect =
  "h-6 rounded-[var(--radius-ui)] border border-border bg-bg px-1 text-[11.5px] text-fg outline-none focus:border-accent";

export function RefSceneRow({
  item,
  note,
  onNote,
  onRemove,
  role = "scene",
  onRole,
}: {
  item: ReferenceLite;
  note: string;
  onNote: (text: string) => void;
  onRemove: () => void;
  /** «сцена» (умолчание) или «внешность модели» (№318) */
  role?: RefRole;
  onRole?: (next: RefRole) => void;
}) {
  const w = item.asset_width;
  const h = item.asset_height;
  const sized = w !== null && h !== null;
  const original = item.note ?? "";
  const face = role === "face";

  return (
    <div className="flex gap-3 rounded-[var(--radius-ui)] border border-border p-2" data-annot="референс">
      <img src={thumbUrl(item.asset_path)} alt="" className="h-24 w-16 flex-none rounded-[4px] object-cover" />
      <div className="grid min-w-0 flex-1 gap-1 text-[12.5px]">
        <div className="flex items-center gap-2">
          <span className="min-w-0 truncate font-medium text-fg">
            {item.library_name} · {item.category_name}
          </span>
          <Link to={`/lib/ref/${item.id}`} className="flex-none text-[11.5px] text-accent-text hover:underline">
            карточка референса
          </Link>
          {face ? null : (
            <SceneCounter
              scene={{
                kind: "ref",
                id: item.id,
                thumb: item.asset_path,
                title: `${item.library_name} · ${item.category_name}`,
              }}
            />
          )}
          {sized ? <span className="flex-none text-[11.5px] tnum text-fg-faint">{w}×{h}</span> : null}
          {onRole ? (
            <label className="ml-auto flex flex-none items-center gap-1 text-[11.5px] text-fg-muted" title={ROLE_HINT}>
              роль
              <select
                className={roleSelect}
                value={role}
                aria-label={`роль референса ${item.id}`}
                onChange={(e) => onRole(e.target.value as RefRole)}
              >
                {ROLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <button
            type="button"
            title="убрать референс"
            onClick={onRemove}
            className={`${btnGhost} flex-none ${onRole ? "" : "ml-auto"}`}
          >
            <X size={13} />
            убрать
          </button>
        </div>
        {face ? (
          <span className="text-[12px] text-fg-muted">
            входом в каждую пару: с картинки берутся лицо, причёска и типаж — одежда и фон не берутся
          </span>
        ) : null}
        {!face && w !== null && h !== null && Math.max(w, h) < SMALL_SIDE ? (
          <div className="text-[12px] text-warn">
            картинка {w}×{h} — мелкая для сцены: детали интерьера в генерации будут приблизительными; референс из
            Pinterest лучше положить заново кнопкой «в библиотеку» — она качает оригинал
          </div>
        ) : null}
        {face ? null : (
          <>
            <textarea
              className={`${input} min-h-[48px] w-full resize-y py-1`}
              placeholder="что учесть"
              value={note}
              maxLength={NOTE_MAX}
              onChange={(e) => onNote(e.target.value)}
            />
            <span className={label}>уходит в промпт этого запуска; заметка референса не меняется</span>
          </>
        )}
        {!face && original ? (
          <div className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate text-[12px] text-fg-muted" title={original}>
              заметка референса: {original}
            </span>
            <button
              type="button"
              className={`${btnGhost} ml-auto flex-none`}
              disabled={note === original}
              onClick={() => onNote(original)}
            >
              ↺ из заметки
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
